import { defineStore } from 'pinia';
import sidebarPanes from '@/resources/sidebarPanes';
import { usePageStore } from '@/store/pinia/page';
import { usePhaseStore } from '@/store/pinia/phase';
import { useUsersStore } from '@/store/pinia/users';
import { useMeetingStore } from '@/store/pinia/meeting';
import { useFeaturesStore } from '@/store/pinia/features';

import _ from 'underscore';
import { nM } from '@/legacy';
import { nextTick } from 'vue';

function mappedPhase(p) {
  return nM.phaseMap[p];
}

export const useSidebarStore = defineStore('sidebar', {
  state() {
    return {
      currentPhase: 'init',
      trueView: '',
      previous: false,
      tempPrevious: false,
      extra: false,
      extraAnimating: false,
      previousExtra: false,
      current: false,
      requested: false,
      base: false,
      pending: false,
      isEntering: false,
      isLeaving: false,
      isOpening: false,
      isClosing: false,
      isOpen: false,
      latestReason: false,
      latestSkillBar: {},
      phaseSource: 'microskills',
      notice: false,
      backToMenu: false,
      iCancelled: false,
      settingsPaneIsShowing: false,
      iClickedLeave: false,
      forceReady: false,
    };
  },
  getters: {
    me() {
      const { me } = useUsersStore();
      return me;
    },
    sidebarOpen: (state) => state.current !== false,
    meetingMenuOpen: (state) => !!state.current?.inHamburger,
    isAnimating: (state) => state.isEntering || state.isLeaving || state.isOpening || state.isClosing,
    isVisible() {
      return this.sidebarOpen || this.isAnimating;
    },
    po3SettingBackTo() {
      let phaseStore = usePhaseStore();
      if (phaseStore.currentPhase === 'lobby') {
        return 'lobby';
      } else {
        return 'po3';
      }
    },
    isMeeting() {
      let pageStore = usePageStore();
      return pageStore.isMeeting;
    },
    noAnimations() {
      let featuresStore = useFeaturesStore();
      return featuresStore.noAnimations;
    },
    isEnd() {
      let phaseStore = usePhaseStore();
      if (phaseStore.currentPhase === 'endMeeting') {
        return true;
      }
      if (phaseStore.currentPhase === 'finish') {
        return true;
      }

      return this.isMeeting ? false : true;
    },
    mustBeOpen() {
      let phaseStore = usePhaseStore();
      let meetingStore = useMeetingStore();
      let usersStore = useUsersStore();

      if (usersStore.iAmInWaitingRoom && !usersStore.iAmOwner) {
        return false;
      }

      if (phaseStore.currentPhase === 'ended') {
        return false;
      }
      if (phaseStore.currentPhase === 'lobby' && meetingStore.useLobby) {
        return true;
      }
      if (this.isMeeting && phaseStore.currentPhase !== 'freeform') {
        return true;
      }
      if (phaseStore.settingUp) {
        return true;
      }

      return this.isMeeting;
    },
    currentName: (s) => s.current.name,
    isSplit() {
      return !!this.extra;
    },
    skillBarShowing(s) {
      return s.current.name === 'skillBar' && !s.isEntering;
    },
  },
  actions: {
    initMeeting(data, isResync) {
      this.handleSkillBar(data.state, isResync);
      this.checkFields(data.state, isResync);
    },
    onPageChange(page) {
      this.iClickedLeave = false;
      if (page !== 'meetingFrame') {
        this.requestPane(false, { force: true }, 'onPageChange');
        this.clearExtraPane();
      }
    },
    updatePhase(data) {
      this.handleSkillBar({ view: data.phase.module_id });
      this.handleExtraClose({ view: data.phase.module_id });
      this.backToMenu = false;
    },
    updateView(data) {
      let usersStore = useUsersStore();
      if (usersStore.iAmInWaitingRoom) {
        return;
      }
      if (data.view === 'freeform' && nM.setups[data.previous_view]) {
        if (this.backToMenu) {
          if (this.iCancelled) {
            this.iCancelled = false;
            return false;
          } else {
            this.requestPhaseSource();
          }
        } else {
          this.requestPane(false);
        }
      } else {
        this.iCancelled = false;
        this.handleSkillBar(data);
      }
    },
    checkFields({ fields }, isResync) {
      if (!isResync) {
        _.each(fields, (id, field) => {
          if (id === this.me.id) {
            this.requestPane(field, {}, 'locked');
          }
        });
      }
    },
    handleSkillBar(data, isResync) {
      let usersStore = useUsersStore();
      let meetingStore = useMeetingStore();

      let view = data?.view || this.trueView;

      let setupFor = nM.setups[view];
      let phase = mappedPhase(view);
      if (phase === mappedPhase(this.trueView) && isResync) {
        return false;
      }

      this.trueView = view;

      if (phase === 'ended') {
        this.requestPane(false, { force: true, ending: true }, 'end of meeting');
        return false;
      }

      if (!phase) {
        this.requestPane(false);
        return false;
      }

      if (usersStore.iAmInWaitingRoom) {
        this.iAmInWaitingRoom();
        return true;
      }

      if (usersStore.iAmAway) {
        this.requestPane('skillBar', { phase: 'breakTime', barType: 'phase' }, 'on_update_view');
        return true;
      }

      if (usersStore.iAmAwayInGroup) {
        this.requestPane('skillBar', { phase: 'groups', barType: 'phase' }, 'on_update_view');
        return true;
      }

      if (setupFor) {
        this.requestPane('skillBar', { phase: setupFor, barType: 'setTime' }, 'on_update_view');
        return true;
      } else {
        this.currentPhase = phase;
      }

      if (phase === 'lobby') {
        if (meetingStore.useLobby) {
          this.requestPane('lobby', {}, 'on_update_view');
          return true;
        } else {
          this.requestPane(false);
          return false;
        }
      }
      if (phase === 'endMeeting') {
        this.requestPane('endBar', {}, 'on_update_view');
        this.handleExtraClose({ view: 'endBar' });
        return true;
      }

      if (phase === 'freeform') {
        if (this.previous.name === 'lobby' || !this.latestSkillBar.phase) {
          _.delay(() => {
            this.requestPane('microskills', {}, 'on_update_view');
          }, 1500);
        } else {
          this.requestPane('microskills', {}, 'on_update_view');
        }
        return true;
      }
      this.requestPane('skillBar', { phase: phase, barType: 'phase' }, 'on_update_view');
    },
    isExtrable: (v) => !['skillBar', 'microskills', 'topic', 'checkInQuestion', 'proposal', 'endBar', 'outside'].includes(v),
    requestPane(v, args = {}, why = '??') {
      if (!args.hardClose && (!this.isMeeting || this.iClickedLeave) && sidebarPanes[v]?.name && !sidebarPanes[v]?.outOfMeeting) {
        return false;
      }

      let phaseStore = usePhaseStore();

      if (v === 'settings' && this.extra.name === 'settings') {
        this.clearExtraPane();
        return false;
      }

      // If it should open in extra, do that
      let putInExtra = false;
      if (this.isMeeting && !this.iClickedLeave) {
        if (args.isExtra) {
          putInExtra = true;
        }

        if (this.isExtrable(v)) {
          if (phaseStore.current !== 'freeform' && this.current.barType === 'phase') {
            putInExtra = true;
          }
          if (this.current.barType === 'setTime' && this.current.phase === 'groups') {
            putInExtra = true;
          }
        }
      }

      if (putInExtra) {
        this.requestExtraPane(v, args);
        return false;
      }

      // handle if you triggered the change yourself

      if (args.iStarted) {
        this.backToMenu = true;
      }

      // Track the skill bar details in case we need to show something else a while
      if (v === 'skillBar') {
        this.latestSkillBar = args;
      }

      let { force, ending, local } = args;

      // Make sure it is a real pane
      if (v && !sidebarPanes[v]) {
        console.log('[err] not a real sidebar pane');
      }

      this.latestReason = why;

      if (v) {
        this.requested = { ...sidebarPanes[v], ...args };
      } else {
        // make sure you con only close the sidebar if not in a meeting with a guide
        let usersStore = useUsersStore();

        if (force || ending) {
          this.requested = false;
        } else if (this.isMeeting && !usersStore.iAmInWaitingRoom) {
          if (phaseStore.current === 'endMeeting') {
            this.requested = { ...sidebarPanes.endBar, ...args };
          } else if (phaseStore.current === 'ended') {
            this.requested = false;
          } else if (phaseStore.currentPhase !== 'freeform') {
            this.requestPane('skillBar', this.latestSkillBar, 'overdue');
            return false;
          } else {
            this.requested = { ...sidebarPanes.microskills, ...args };
          }
        } else {
          this.requested = false;
        }
      }

      // Prevent back-to loops
      if (!this.current.noBackTo && !args.resetBase) {
        this.previous = { ...this.current };
      }

      // Save an extra back for when you're looking at an overlay like settings
      if (this.requested.temp) {
        this.tempPrevious = { ...this.current };
      } else {
        this.tempPrevious = false;
      }

      // Clear where you go back to for some weird annoying cases
      if (args.resetBase) {
        this.previous = false;
        this.base = false;
      }

      // If ok, change the pane. If not, say we have one ready for when the current one is finished with, to prevent interrupts.
      if (!this.current.noInterrupt || force || local || this.requested.temp) {
        this.changePane(args);
        this.pending = false;
      } else {
        this.pending = true;
      }
    },
    changePane(args = {}) {
      if (this.requested) {
        if (this.current?.name !== this.requested?.name && !this.noAnimations) {
          this.isEntering = true;
        }

        nextTick(() => {
          if (this.requested.inHamburger && !this.current.inHamburger && !this.current.isEditPane && !args.resetBase && this.current.barType !== 'info') {
            this.base = this.current ? { ...this.current } : false; // Controls where we go back to when you close the hamburger menu.
          }
          this.current = { ...this.requested };

          // Set where to go back to after skillBar
          if (this.current.name === 'microskills') {
            this.phaseSource = 'microskills';
          }
          if (this.current.name === 'whatNext') {
            this.phaseSource = 'microskills';
          }
        });
      } else {
        this.current = false;
      }
    },
    finishedWithPane(extra) {
      // firat check if it is from extra
      if (extra) {
        if (this.previousExtra) {
          this.requestExtraPane({ ...this.previousExtra });
        } else if (this.previousExtra.backTo) {
          this.requestExtraPane({ ...this.previousExtra.backTo });
        } else {
          this.clearExtraPane();
        }
      } else {
        if (this.pending) {
          this.changePane();
        } else {
          this.requestBack({ force: true });
        }
      }
    },
    requestPhaseSource(args = {}) {
      if (args.iCancelled) {
        this.iCancelled = true;
      }
      this.requestPane(this.phaseSource, { ...args, resetBase: true }, 'requestPhaseSource');
    },
    requestBack(args = {}) {
      if (this.current.backTo) {
        if (this.current.backTo === 'base') {
          const phaseStore = usePhaseStore();
          if (phaseStore.settingUp || phaseStore.currentPhase !== 'freeform') {
            this.current.backTo = 'skillBar';
          } else {
            this.current.backTo = 'microskills';
          }
        }

        if (this.current.backTo === 'skillBar') {
          args = { ...this.latestSkillBar, ...args };
        }
        if (this.current.backTo === 'po3') {
          this.requestPane(this.po3SettingBackTo, { ...args, force: true }, 'back');
        } else {
          this.requestPane(this.current.backTo, { ...args, force: true }, 'back');
        }
      } else if (this.previous) {
        if (this.current.name === 'po3' && this.previous.isEditPane) {
          this.requestPane(false, args, 'back');
        } else {
          this.requestPane(this.previous.name, { ...this.previous, ...args }, 'back');
        }
      } else {
        if (this.inMeeting) {
          this.requestPane('microskills', args, 'back');
        } else {
          if (this.currentPhase === 'lobby') {
            this.requestPane(false, args, 'back');
          } else if (this.current.backTo) {
            this.requestPane(this.current.backTo, args, 'back');
          } else {
            this.requestPane(false, args, 'back');
          }
        }
      }
    },
    requestExtraPane(v, args = {}) {
      this.previousExtra = { ...this.extra };

      if (typeof v === 'string') {
        this.extra = { ...sidebarPanes[v], ...args };
      } else {
        this.extra = { ...v, ...args };
      }
    },
    clearExtraPane() {
      this.previousExtra = { ...this.extra };
      this.extra = false;
    },
    handleExtraClose({ view }) {
      if (!['freeform', 'endBar', 'outside'].includes(view)) {
        return false;
      }
      if (!this.extra.isEditPane || view === 'outside') {
        this.clearExtraPane();
      }
    },
    closeForTimedTemp(v) {
      if (v === this.extra.name) {
        this.clearExtraPane();
      } else {
        this.requestBack();
      }
    },
    closeHamburger() {
      if (this.tempPrevious) {
        let args = this.tempPrevious.name === 'skillBar' ? this.latestSkillBar : {};
        this.requestPane(this.tempPrevious.name, args, 'closingHamburger to tempPrevious');
        this.tempPrevious = false;
      } else if (this.base) {
        let args = this.base.name === 'skillBar' ? this.latestSkillBar : {};
        this.requestPane(this.base.name, args, 'closingHamburger to base');
      } else {
        this.requestPane(false, {}, 'closingHamburger to close');
      }
    },
    closeSidebar({ hardClose }) {
      this.requestPane(false, { force: true, hardClose }, 'closeSidebar');
      this.clearExtraPane();
    },
    meetingOver() {
      this.requestPane(false, { force: true, ending: true }, 'meetingOver');
    },
    toggleSettings() {
      if (this.current.name === 'settings') {
        this.requestBack();
      } else {
        this.requestPane('settings');
      }
    },
    usersUnaway({ users }) {
      let usersStore = useUsersStore();
      if (users.filter((u) => usersStore.isMe(u.id)).length > 0) {
        _.delay(() => {
          this.handleSkillBar({ view: this.trueView });
        }, 10);
      }
    },
    usersReturned({ users }) {
      let usersStore = useUsersStore();
      if (users.filter((u) => usersStore.isMe(u.id)).length > 0) {
        _.delay(() => {
          this.handleSkillBar({ view: this.trueView });
        }, 10);
      }
    },
    updateNotice(v) {
      this.notice = v;
    },
    iAmOverdue() {
      if (this.current.name !== 'skillBar') {
        this.requestPane('skillBar', this.latestSkillBar, 'overdue');
      }
    },
    iAmInWaitingRoom() {
      this.requestPane(false, this.latestSkillBar, 'waiting');
    },
    closeParticipantList() {
      if (this.extra.name === 'participantList') {
        this.clearExtraPane();
      } else if (this.current.name === 'participantList') {
        this.requestBack();
      }
    },
    showSettingsPane() {
      this.settingsPaneIsShowing = true;
    },
    hideSettingsPane() {
      this.settingsPaneIsShowing = false;
    },
    setIsOpening(v) {
      this.isEntering = v;
    },
    setIsOpen(v) {
      this.isOpen = v;
    },
    setIsClosing(v) {
      this.isClosing = v;
    },
    setIsEntering(v) {
      this.isEntering = v;
    },
    setIsLeaving(v) {
      this.isLeaving = v;
    },
    setExtraAnimating(v) {
      this.extraAnimating = v;
    },
    setIClickedLeave(v) {
      this.iClickedLeave = v;
    },
    setForceReady(v) {
      this.forceReady = v;
    },
    checkForBar(attempt = 0) {
      // First check we actually need to show a bar
      if (!this.mustBeOpen) {
        return false;
      }

      // Check if we have a bar open by checking width of element on the page
      const mainContents = document.querySelector('#mainSidebar > *')?.clientWidth;
      const nav = document.querySelector('#sidebarNav')?.clientWidth;

      if (!!mainContents && !!nav) {
        return false;
      }
      if (!this.forceReady) {
        this.setForceReady(true);
        setTimeout(() => {
          this.checkForBar(attempt + 1);
        }, 1000);
      }
      // If so, force it open
      // then check which bar it should be
    },
    fixWrongPane(wrongPane) {
      if (wrongPane === 'microskills') {
        console.log('wrong pane microskills');
        this.handleSkillBar(false, false);
        // check
      }
    },
  },
});
